import { css } from 'aphrodite';
import * as React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { FormFieldType } from '../../../../../../../../extViewmodels';
import { Checkbox } from '../../../../../../../../web/components/Checkbox';
import { ISelectBoxOption, SelectBox } from '../../../../../../../../web/components/SelectBox';
import { TextInput } from '../../../../../../../../web/components/TextInput';
import { baseStyleSheet } from '../../../../../../../../web/styles/styles';
import { IFormField } from '../../../../../../../models/Api';
import { styleSheet } from '../styles';
import { ErrorMessage } from './ErrorMessage';

interface CustomFormFieldsProps {
	fields: IFormField<any>[];
}

export const CustomFormFields: React.FC<CustomFormFieldsProps> = ({ fields }) => {
	const {
		control,
		formState: { errors },
		setValue,
	} = useFormContext();

	return (
		<>
			{fields
				?.filter(f => !f.isHidden)
				.map(f => {
					const fieldDropdownOptions = f.options?.map(o => {
						return {
							id: o.id,
							title: o.label,
							value: o.label,
						};
					});

					return (
						<div key={f.id} className={css(styleSheet.formControlFullWidth)}>
							<label htmlFor={f.id} className={css(styleSheet.label)}>
								{f.label}
								{!f.isOptional ? (
									<>
										{' '}
										<span className={css(baseStyleSheet.required)}>*</span>
									</>
								) : null}
							</label>
							<Controller
								name={`customProperties.${f.id}`}
								control={control}
								render={({ field: { ref, ...fieldProps } }) => {
									if (f.fieldType === FormFieldType.String) {
										return (
											<>
												<TextInput
													type='text'
													inputId={f.id}
													inputClassName={css(styleSheet.textInput)}
													value={fieldProps.value || ''}
													{...fieldProps}
												/>
												{(errors.customProperties as any)?.[f.id] && (
													<ErrorMessage message={(errors.customProperties as any)[f.id]?.message} />
												)}
											</>
										);
									}

									if (f.fieldType === FormFieldType.Option) {
										const onSelectionChanged = (selectedOption: ISelectBoxOption<string>) => {
											setValue(`customProperties.${f.id}`, selectedOption.id);
										};
										const onRenderTriggerTitle = (selectedOption: ISelectBoxOption<string>) => {
											return <span>{selectedOption.title as React.ReactNode}</span>;
										};
										return (
											<>
												<SelectBox
													options={fieldDropdownOptions}
													onRenderSelectedOptionTitle={onRenderTriggerTitle}
													onSelectionChanged={onSelectionChanged}
													placeholder={<span>Select option</span>}
													triggerClassName={css(styleSheet.selectBoxtriggerButton)}
													{...fieldProps}
													selectedOption={fieldDropdownOptions?.find(x => x.id === fieldProps.value) || undefined}
												/>
												{(errors.customProperties as any)?.[f.id] && (
													<ErrorMessage message={(errors.customProperties as any)[f.id]?.message} />
												)}
											</>
										);
									}

									if (f.fieldType === FormFieldType.MultipleOptions) {
										const fieldCheckboxOptions = f.options?.map(o => {
											const fieldCheckboxSelectedOptions: string[] = fieldProps?.value?.split(',') ?? [];
											/**
											 * Method to update the value of the field
											 * @param e {React.ChangeEvent<HTMLInputElement>}
											 * sets a {String} value of the field
											 */
											const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
												const newOptions = [...fieldCheckboxSelectedOptions];
												if (e.target.checked) {
													newOptions.push(o.id);
												} else {
													newOptions.splice(fieldCheckboxSelectedOptions.indexOf(o.id), 1);
												}

												setValue(`customProperties.${f.id}`, `${newOptions.join(',')}`);
											};
											/**
											 * Use the onChange method to update the value of the field
											 */
											fieldProps.onChange = onChange;
											return (
												<Checkbox
													key={o.id}
													id={o.id}
													className={css(styleSheet.checkbox)}
													checked={fieldCheckboxSelectedOptions?.includes(o.id)}
													{...fieldProps}
												>
													<span>{o.label}</span>
												</Checkbox>
											);
										});
										return (
											<>
												{fieldCheckboxOptions}
												{(errors.customProperties as any)?.[f.id] && (
													<ErrorMessage message={(errors.customProperties as any)[f.id]?.message} />
												)}
											</>
										);
									}

									return null;
								}}
							/>
						</div>
					);
				})}
		</>
	);
};
